// A landing page for traders at one prop firm, e.g. /topstep-trading-journal: what to
// watch on that firm's accounts, its rules (from the presets the journal uses) and how
// Market Matrix tracks them. The same text is sent to search engines by prerender.js.
import { Link } from 'react-router-dom';
import { PRESETS, PRESETS_CHECKED } from './balance';
import { FIRM_PAGES, findFirmPage, firmRows, fullName } from './firmPages';
import { PublicShell, localPath, usePageLanguage, useTitle } from './PublicPage';
import { usd } from './pnl';
import { useI18n } from './i18n';

const SHOTS = {
  rules: { en: [1584, 746], es: [1584, 872] },
  insights: { en: [1984, 950], es: [1984, 1034] },
  session: { en: [1984, 1546], es: [1984, 1576] },
};

function Shot({ name, lang, alt }) {
  const [width, height] = SHOTS[name][lang === 'es' ? 'es' : 'en'];
  return <img src={`/home-${name}${lang === 'es' ? '-es' : ''}.jpg`} width={width} height={height} alt={alt} loading="lazy" />;
}

export function drawdownLabel(row) {
  if (row.drawdownType === 'static') return 'Static';
  const kind = row.drawdownType === 'trailing_intraday' ? 'Intraday trailing' : 'End-of-day trailing';
  return kind;
}

export function consistencyLabel(row) {
  if (!row.consistency) return 'No rule';
  return row.consistency.base === 'target' ? 'Best day ≤ {pct}% of the profit target' : 'Best day ≤ {pct}% of total profit';
}

// Shown when a slug isn't one of the firm pages (the route list comes from FIRM_PAGES).
export default function FirmPage({ user, slug, forceLang }) {
  const page = findFirmPage(slug);
  const lang = usePageLanguage(forceLang, `/${slug}`);
  const { t } = useI18n();
  const name = page.name;
  useTitle(t('{firm} Trading Journal: Track Your Rules, Drawdown and Payouts | Market Matrix', { firm: name }));
  const copy = page[lang] || page.en;
  const rows = firmRows(PRESETS, page.firm);
  const money = (n) => (n ? usd.format(n).replace(/\.00$/, '') : '—');
  const others = FIRM_PAGES.filter((p) => p.slug !== slug);

  return (
    <PublicShell user={user} className="firm-page">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Trading journal for {firm} traders', { firm: name })}</p>
        <h1>{t('{firm} trading journal', { firm: name })}</h1>
        <p className="subtitle">{copy.intro}</p>
        <div className="firm-cta">
          <Link to={user ? '/app' : '/signup'} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
          <Link to={localPath(lang, '/')} className="firm-secondary">{t('See how it works')}</Link>
        </div>
        {!user && <p className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>}
      </header>

      <section className="firm-section">
        <h2>{t('What to watch on a {firm} account', { firm: name })}</h2>
        <ul className="firm-watch">
          {copy.watch.map((w) => <li key={w}>{w}</li>)}
        </ul>
      </section>

      <section className="firm-section">
        <h2>{t('{firm} rules, built in', { firm: name })}</h2>
        <p>{t('Pick your {firm} account from the presets and these rules are filled in for you. Every number stays editable.', { firm: name })}</p>
        <div className="table-wrap">
          <table className="firm-rules">
            <thead>
              <tr>
                <th scope="col">{t('Plan')}</th>
                <th scope="col">{t('Account')}</th>
                <th scope="col">{t('Profit target')}</th>
                <th scope="col">{t('Max loss')}</th>
                <th scope="col">{t('Drawdown')}</th>
                <th scope="col">{t('Daily loss limit')}</th>
                <th scope="col">{t('Consistency')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.plan}-${r.size}`}>
                  <td>{t(r.plan)}{r.payout && <small className="firm-rule-sub">{t('payout after {n} winning days of {min}+', { n: r.payout.days, min: money(r.payout.min) })}</small>}</td>
                  <td>{money(r.size)}</td>
                  <td>{money(r.profitTarget)}</td>
                  <td>{money(r.maxLossLimit)}</td>
                  <td>{t(drawdownLabel(r))}{r.lockOffset > 0 && <small className="firm-rule-sub">{t('stops at the start + {amount}', { amount: money(r.lockOffset) })}</small>}</td>
                  <td>{r.dailyLossLimit ? money(r.dailyLossLimit) : t('No rule')}</td>
                  <td>{r.consistency ? t(consistencyLabel(r), { pct: r.consistency.limit }) : t('No rule')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="firm-note">
          {t('Rules last checked {month}. Prop firms change their rules often, so always confirm the current terms on {firm}’s own site.', { month: t(PRESETS_CHECKED), firm: name })}
        </p>
      </section>

      <section className="firm-section">
        <h2>{t('How Market Matrix tracks your {firm} account', { firm: name })}</h2>
        <div className="firm-features">
          <article>
            <div>
              <h3>{t('Your limits, in dollars')}</h3>
              <p>{t('Import your trades and the rules panel shows how far you are from passing, from your max loss and from today’s limit, with a green, yellow or red status. It works from the trades you import; it doesn’t connect to {firm} or see open positions.', { firm: name })}</p>
            </div>
            <Shot name="rules" lang={lang} alt={t('Rules panel for a 50K evaluation: distance to the profit target, the max loss and today’s limit, and the consistency rule')} />
          </article>
          <article>
            <div>
              <h3>{t('What’s costing you money')}</h3>
              <p>{t('Insights reads your trades and journal and shows your best and worst times of day, setups and habits, so you fix what actually costs you before it costs you the account.')}</p>
            </div>
            <Shot name="insights" lang={lang} alt={t('Insights showing what’s working, what’s costing you and what to try next')} />
          </article>
          <article>
            <div>
              <h3>{t('A routine that holds on bad days')}</h3>
              <p>{t('Morning prep before the open, a calm Live session screen with your rules and reset buttons for FOMO and tilt, and an end-of-day reflection. Most accounts are lost to habits, not setups.')}</p>
            </div>
            <Shot name="session" lang={lang} alt={t('Live session with today’s risk, ranked rules, reset buttons and a session log')} />
          </article>
        </div>
      </section>

      <section className="firm-section">
        <h2>{t('Set up in three steps')}</h2>
        <ol className="firm-steps">
          <li><strong>{t('Add your account.')}</strong> {t('Choose the {firm} preset that matches your plan and size.', { firm: name })}</li>
          <li><strong>{t('Import your trades.')}</strong> {t('Upload your platform’s export (TopstepX, Tradovate, NinjaTrader web or any CSV), or add trades by hand.')}</li>
          <li><strong>{t('Know where you stand.')}</strong> {t('See your room left, your progress to the target and what to work on, after every import.')}</li>
        </ol>
      </section>

      <section className="firm-section">
        <h2>{t('Questions {firm} traders ask', { firm: name })}</h2>
        <dl className="firm-faq">
          <dt>{t('Does Market Matrix connect to my {firm} account?', { firm: name })}</dt>
          <dd>{page.firm === 'Topstep' ? t('Yes, through TopstepX: connect it once with your TopstepX API Access and your trades sync about every 15 minutes, with your plan’s rules loaded for you. It only reads your trades and never places orders. You can also import a file or add trades by hand.') : t('No. You import a file from your trading platform or add trades by hand, and your rules panel updates each time. It never sees open positions and never places trades.')}</dd>
          <dt>{t('Is Market Matrix part of {firm}?', { firm: name })}</dt>
          <dd>{t('No. Market Matrix is an independent trading journal and isn’t affiliated with {firm}.', { firm: fullName(page) })}</dd>
          <dt>{t('What if {firm} changes its rules?', { firm: name })}</dt>
          <dd>{t('Every number in your account stays editable, so you can match any change right away. We review the presets regularly too.')}</dd>
          <dt>{t('Is it free?')}</dt>
          <dd>{t('Every new member gets 14 days of every feature, free. After that, the core journal stays free for good on one account, including automatic TopstepX and Interactive Brokers sync, trade charts, your P&L calendar and stats, your prop firm rules panel and your daily routine. Pro ($19 a month) adds unlimited accounts, Insights and the full Notebook.')}</dd>
        </dl>
      </section>

      <section className="firm-section firm-links">
        <h2>{t('Learn the rules')}</h2>
        <ul>
          <li><Link to={localPath(lang, '/blog/trailing-drawdown-explained')}>{t('Trailing drawdown explained')}</Link></li>
          <li><Link to={localPath(lang, '/blog/consistency-rule-explained')}>{t('The consistency rule explained')}</Link></li>
          <li><Link to={localPath(lang, '/blog/how-to-pass-a-prop-firm-challenge')}>{t('How to pass a prop firm challenge')}</Link></li>
          <li><Link to={localPath(lang, '/blog/choose-prop-firm-account-size')}>{t('How to choose your account size')}</Link></li>
          <li><Link to={localPath(lang, '/tools/drawdown')}>{t('Drawdown calculator')}</Link></li>
        </ul>
        <h2>{t('Trading journals for other prop firms')}</h2>
        <ul>
          {others.map((p) => <li key={p.slug}><Link to={localPath(lang, `/${p.slug}`)}>{t('{firm} trading journal', { firm: p.name })}</Link></li>)}
        </ul>
      </section>

      <section className="final-cta blog-final-cta">
        <h2>{t('Know your {firm} limits before you hit them', { firm: name })}</h2>
        <Link to={user ? '/app' : '/signup'} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
        <p className="firm-note">{t('Market Matrix is an independent journal and isn’t affiliated with or endorsed by {firm}. Trading futures involves substantial risk of loss.', { firm: fullName(page) })}</p>
      </section>
    </PublicShell>
  );
}
